/** Layout primitives: Stack (vertical), Row (horizontal), Grid (auto-fill), Container and Screen. */
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

type Space = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
const gapOf = (g: Space | undefined): string | undefined => (g === undefined ? undefined : g === 0 ? '0' : `var(--space-${g})`);

interface BoxProps extends HTMLAttributes<HTMLDivElement> {
  gap?: Space;
  children?: ReactNode;
  as?: 'div' | 'section' | 'ul' | 'ol' | 'nav' | 'header' | 'footer' | 'main';
}

export function Stack({ gap = 3, className, style, children, as: tag = 'div', ...rest }: BoxProps) {
  const Tag = tag as 'div';
  return (
    <Tag className={cx('stack', className)} style={{ gap: gapOf(gap), ...style }} {...rest}>
      {children}
    </Tag>
  );
}

export interface RowProps extends BoxProps {
  wrap?: boolean;
  justify?: 'start' | 'between' | 'end';
  align?: 'center' | 'start';
}

export function Row({ gap = 2, wrap, justify = 'start', align = 'center', className, style, children, as: tag = 'div', ...rest }: RowProps) {
  const Tag = tag as 'div';
  return (
    <Tag
      className={cx('row', wrap && 'row--wrap', justify === 'between' && 'row--between', justify === 'end' && 'row--end', align === 'start' && 'row--start', className)}
      style={{ gap: gapOf(gap), ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export interface GridProps extends BoxProps {
  /** Minimum column width in px before wrapping. */
  min?: number;
}

export function Grid({ gap = 3, min = 240, className, style, children, as: tag = 'div', ...rest }: GridProps) {
  const Tag = tag as 'div';
  return (
    <Tag className={cx('grid', className)} style={{ gap: gapOf(gap), ['--grid-min' as string]: `${min}px`, ...style } as CSSProperties} {...rest}>
      {children}
    </Tag>
  );
}

export interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  max?: number;
  children?: ReactNode;
}

export function Container({ max = 1120, className, style, children, ...rest }: ContainerProps) {
  return (
    <div className={cx('container', className)} style={{ ['--container-max' as string]: `${max}px`, ...style } as CSSProperties} {...rest}>
      {children}
    </div>
  );
}

export interface ScreenProps {
  /** Max content width. */
  max?: number;
  children?: ReactNode;
  className?: string;
  /** Labelled landmark name. */
  label?: string;
}

/** A full-height page with the situation-room backdrop and a centered container. */
export function Screen({ max, children, className, label }: ScreenProps) {
  return (
    <main className={cx('screen', className)} id="main" aria-label={label}>
      <Container max={max}>{children}</Container>
    </main>
  );
}

export interface ScreenHeaderProps {
  title: ReactNode;
  eyebrow?: ReactNode;
  sub?: ReactNode;
  leading?: ReactNode;
  actions?: ReactNode;
}

export function ScreenHeader({ title, eyebrow, sub, leading, actions }: ScreenHeaderProps) {
  return (
    <header className="screen-header">
      {leading}
      <div className="grow">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1 className="screen-header__title">{title}</h1>
        {sub && <p className="screen-header__sub">{sub}</p>}
      </div>
      {actions}
    </header>
  );
}
